import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, X, CheckCircle2, FileSignature, Send, Sparkles } from 'lucide-react';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { removeMany, confirmCount } from '../../lib/bulkRows';
import { fmt } from '../../lib/accountingStore';
import { apiEstimates, apiCustomers, apiInvoices, email } from '../../lib/api';
import { fmtDateUS, nextNumber } from '../../lib/invoiceItems';
import SmartEstimateDialog from './SmartEstimateDialog';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

import { today as localToday, ymd as localYmd, parseDay } from '../../lib/dates';
import SendForSignatureDialog from '../forms/SendForSignatureDialog';
const sBadge = (s) => ({ Accepted: 'bg-emerald-100 text-emerald-700', Pending: 'bg-blue-100 text-blue-700', Declined: 'bg-red-100 text-red-700', Expired: 'bg-slate-100 text-slate-700' }[s] || 'bg-slate-100 text-slate-700');

export default function Estimates() {
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const [signFor, setSignFor] = useState(null);
  const load = useCallback(() => {
    apiEstimates.list().then((d) => setEstimates(d || [])).catch(() => setEstimates([]));
    apiCustomers.list().then((d) => setCustomers(d || [])).catch(() => setCustomers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSend = async (est) => {
    const t = toast.loading(`Emailing ${est.id}…`);
    try { const r = await email.sendEstimate(est.id); toast.success(`Emailed to ${r.to}`, { id: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send', { id: t }); }
  };
  const onDelete = async (id) => { try { await apiEstimates.remove(id); load(); toast.success('Estimate deleted'); } catch { toast.error('Failed'); } };
  // The conversion itself, with the invoice book passed in. A batch reads that
  // book once and adds each new invoice to it, because the next number is read
  // from it: refetching per estimate is slow, and not advancing it at all would
  // hand two estimates the same invoice number.
  const convertOne = async (est, book) => {
    const num = nextNumber(book, 6000);
    const cust = customers.find((c) => c.id === est.customerId);
    const inv = {
      id: `INV-${num}`, number: String(num), customerId: est.customerId,
      customerName: cust?.name || est.customerName || '', customerAccountNumber: cust?.accountNumber || '',
      date: fmtDateUS(new Date()), due: fmtDateUS(new Date(Date.now() + 30 * 86400000)),
      status: 'Open', items: est.items || [], total: est.total, subtotal: est.total, paid: 0, amountDue: est.total,
      createdDate: fmtDateUS(new Date()),
    };
    await apiInvoices.create(inv);
    await apiEstimates.update(est.id, { ...est, status: 'Accepted' });
    book.push(inv);
    return inv.id;
  };

  const onConvert = async (est) => {
    try {
      const book = await apiInvoices.list().catch(() => []);
      const id = await convertOne(est, book);
      load(); toast.success(`Converted to ${id}`);
    } catch { toast.error('Convert failed'); }
  };

  // Ticking rows. Converting a season's accepted quotes one at a time is the
  // same trip through a row menu that billing jobs was.
  const sel = useSelection(estimates.map((e) => e.id));
  const picked = () => estimates.filter((e) => sel.has(e.id));

  const bulkConvert = async () => {
    const rows = picked().filter((e) => e.status !== 'Accepted');
    const already = sel.count - rows.length;
    if (!rows.length) {
      toast.error(already ? 'Those are already accepted and invoiced' : 'Nothing to convert');
      return;
    }
    if (!confirmCount('Turn', rows.length, 'estimate', 'into invoices?')) return;
    const book = await apiInvoices.list().catch(() => []);
    let done = 0;
    const failed = [];
    for (const est of rows) {
      // eslint-disable-next-line no-await-in-loop
      try { await convertOne(est, book); done += 1; } catch { failed.push(est.id); }
    }
    sel.clear();
    load();
    if (failed.length) {
      toast.error(`Invoiced ${done}; ${failed.length} failed (${failed[0]})`);
    } else {
      toast.success(`${done} estimate(s) invoiced${already ? ` · ${already} already accepted` : ''}`);
    }
  };

  const bulkSend = async () => {
    const rows = picked();
    if (!rows.length) return;
    if (!confirmCount('Email', rows.length, 'estimate', 'to their customers?')) return;
    let done = 0;
    const failed = [];
    for (const est of rows) {
      // eslint-disable-next-line no-await-in-loop
      try { await email.sendEstimate(est.id); done += 1; }
      catch (e) { failed.push(est.id); }
    }
    sel.clear();
    if (failed.length) toast.error(`Emailed ${done}; ${failed.length} could not be sent (${failed[0]})`);
    else toast.success(`Emailed ${done} estimate(s)`);
  };

  const bulkDelete = async () => {
    const ids = sel.chosen;
    if (!ids.length) return;
    if (!confirmCount('Delete', ids.length, 'estimate', 'This cannot be undone.')) return;
    await removeMany(apiEstimates, ids, 'estimate');
    sel.clear();
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-estimates">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Estimates</h2><p className="text-slate-500 text-sm">Send quotes that customers can accept.</p></div>
        <div className="flex gap-2">
          <Button onClick={() => setSmartOpen(true)} variant="outline" className="border-[#8BC53F] text-[#6FA82C] gap-1.5" data-testid="smart-estimate-btn"><Sparkles className="w-4 h-4" />Smart Estimate</Button>
          <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-estimate-btn"><Plus className="w-4 h-4" />New Estimate</Button>
        </div>
      </div>
      <Card className="border-slate-200 overflow-x-auto">
        <BulkBar count={sel.count} onClear={sel.clear} testId="estimates-bulk-bar"
                 countTestId="estimates-bulk-count">
          <Button size="sm" onClick={bulkConvert} className="h-8 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C] text-white" data-testid="estimates-bulk-convert-btn">
            <CheckCircle2 className="w-3.5 h-3.5" />Convert to invoices
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" onClick={bulkSend} data-testid="estimates-bulk-send-btn">
            <Send className="w-3.5 h-3.5" />Email
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1 bg-white text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="estimates-bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />Delete
          </Button>
        </BulkBar>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
            <tr><th className="px-5 py-3 text-left w-10"><TickBox checked={sel.allPicked} onChange={sel.toggleAll} label="Select every estimate listed" testId="estimates-select-all"/></th><th className="px-5 py-3 text-left">Estimate</th><th className="px-5 py-3 text-left">Customer</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Expires</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {estimates.map((est) => {
              const c = customers.find((x) => x.id === est.customerId);
              return (
                <tr key={est.id} className={`hover:bg-slate-50 ${sel.has(est.id) ? 'bg-[#8BC53F]/5' : ''}`}>
                  <td className="px-5 py-4">
                    <TickBox checked={sel.has(est.id)} onChange={() => sel.toggle(est.id)}
                             label={`Select estimate ${est.id}`} testId={`estimates-select-${est.id}`}/>
                  </td>
                  <td className="px-5 py-4 font-semibold text-[#6FA82C]">{est.id}</td>
                  <td className="px-5 py-4 font-medium text-slate-900">{c?.name || est.customerName || 'Unknown'}</td>
                  <td className="px-5 py-4 text-slate-600">{est.date}</td>
                  <td className="px-5 py-4 text-slate-600">{est.expires}</td>
                  <td className="px-5 py-4"><Badge className={`${sBadge(est.status)} border-0`}>{est.status}</Badge></td>
                  <td className="px-5 py-4 text-right font-bold tabular-nums">{fmt(est.total)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => onSend(est)} title="Email estimate"><Send className="w-4 h-4 text-slate-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setSignFor(est)} title="Send for e-signature" data-testid={`esign-${est.id}`}><FileSignature className="w-4 h-4 text-[#6FA82C]" /></Button>
                      {est.status !== 'Accepted' && <Button size="sm" variant="ghost" onClick={() => onConvert(est)} title="Convert to Invoice"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => onDelete(est.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {estimates.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500"><FileSignature className="w-8 h-8 text-slate-300 mx-auto mb-2" />No estimates yet</td></tr>}
          </tbody>
        </table>
      </Card>
      <SendForSignatureDialog open={!!signFor} onClose={() => setSignFor(null)} kind="estimate" estimate={signFor} />
      <EstimateDialog open={open} setOpen={setOpen} customers={customers} onSave={load} />
      <SmartEstimateDialog open={smartOpen} setOpen={setSmartOpen} customers={customers} onSave={load} />
    </div>
  );
}

function EstimateDialog({ open, setOpen, customers, onSave }) {
  const [customerId, setCustomerId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [expires, setExpires] = useState('');
  const [items, setItems] = useState([{ desc: '', qty: 1, rate: 0 }]);
  useEffect(() => {
    if (open) {
      setCustomerId(customers[0]?.id || '');
      setDate(localToday());
      const d = new Date(); d.setDate(d.getDate() + 30); setExpires(localYmd(d));
      setItems([{ desc: '', qty: 1, rate: 0 }]);
    }
  }, [open, customers]);
  const total = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const updateItem = (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; setItems(a); };
  const submit = async () => {
    if (!customerId) { toast.error('Choose customer'); return; }
    const cleaned = items.filter((i) => i.desc); if (!cleaned.length) { toast.error('Add at least one line'); return; }
    const cust = customers.find((c) => c.id === customerId);
    try {
      await apiEstimates.create({ id: `EST-${Date.now().toString(36)}`, customerId, customerName: cust?.name || '', date: fmtDateUS(parseDay(date)), expires: fmtDateUS(parseDay(expires)), status: 'Pending', items: cleaned, total });
      onSave(); setOpen(false); toast.success('Estimate created');
    } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="estimate-dialog">
        <DialogHeader><DialogTitle>New Estimate</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer</Label><Select value={customerId} onValueChange={setCustomerId}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger><SelectContent className="max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Expires</Label><Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="border border-slate-200 rounded-lg">
            <div className="grid grid-cols-[1fr_80px_100px_100px_40px] gap-2 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 uppercase"><div>Description</div><div>Qty</div><div>Rate</div><div className="text-right">Amount</div><div /></div>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_100px_100px_40px] gap-2 px-3 py-2 items-center border-t border-slate-100">
                <Input value={it.desc} onChange={(e) => updateItem(i, 'desc', e.target.value)} className="h-9" />
                <Input type="number" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="h-9" />
                <Input type="number" value={it.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} className="h-9" />
                <div className="text-right font-semibold tabular-nums">{fmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}</div>
                <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setItems([...items, { desc: '', qty: 1, rate: 0 }])} className="w-full text-sm text-[#6FA82C] py-2 border-t border-slate-100 font-semibold hover:bg-emerald-50">+ Add Line</button>
          </div>
          <div className="flex justify-end items-center gap-4"><span className="text-slate-500">Total</span><span className="text-2xl font-extrabold tabular-nums">{fmt(total)}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="estimate-save-btn">Create Estimate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
